import { useEffect, useRef, useState } from 'react';
import { Button, Icon } from '@meu-mapa-nev/design-system';
import { contaApi, type ParticipantDocument, type DocumentFormat } from './api.js';

const STATUS: Record<ParticipantDocument['state'], string> = {
  not_requested: 'Seu PDF está aguardando preparação.',
  queued: 'Seu PDF está na fila de preparação.',
  processing: 'Estamos preparando seu PDF.',
  retry_wait: 'A preparação do PDF será tentada novamente automaticamente.',
  available: 'Seu PDF está pronto.',
  failed: 'Não foi possível preparar o PDF.',
  superseded: 'Esta versão do PDF foi substituída.',
  invalidated: 'Esta versão do PDF está indisponível.',
  deleted: 'Este PDF não está mais disponível.',
};
export function DocumentoDaJornada({
  documents,
  kind,
  downloadingId,
  onDownload,
  queryFailed = false,
  onRecheck,
  onRequested,
  participationId,
  api = contaApi,
}: {
  readonly onRequested?:
    ((kind: string, format: string, baseline: string | undefined) => void) | undefined;
  readonly participationId?: string | undefined;
  readonly api?: typeof contaApi | undefined;
  readonly documents: readonly ParticipantDocument[];
  readonly kind: ParticipantDocument['kind'];
  readonly downloadingId?: string | null | undefined;
  readonly onDownload?: ((id: string) => void) | undefined;
  readonly queryFailed?: boolean | undefined;
  readonly onRecheck?: (() => void) | undefined;
}) {
  const [format, setFormat] = useState<DocumentFormat>('mobile');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requested, setRequested] = useState(false);
  const operation = useRef<{ identity: string; key: string } | null>(null);
  const generation = useRef(0);
  const requestedBaseline = useRef<string | undefined>();
  useEffect(() => {
    generation.current += 1;
    setFormat('mobile');
    setBusy(false);
    setError(null);
    setRequested(false);
    operation.current = null;
    return () => {
      generation.current += 1;
    };
  }, [participationId, kind]);
  const current = documents
    .filter((item) => item.kind === kind && (item.format ?? 'a4') === format)
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
  const awaitingArtifact = requested && current?.id === requestedBaseline.current;
  const pending =
    awaitingArtifact || (current && ['queued', 'processing', 'retry_wait'].includes(current.state));
  const requestDocument = async () => {
    if (!participationId || busy) return;
    const identity = `${participationId}:${kind}:${format}`;
    if (operation.current?.identity !== identity)
      operation.current = { identity, key: crypto.randomUUID() };
    const scope = generation.current;
    setBusy(true);
    setError(null);
    try {
      const result = await api.requestDocument(
        participationId,
        kind === 'participant-initial' ? 'initial' : 'final',
        format,
        operation.current.key,
      );
      if (scope !== generation.current) return;
      if (!result.ok) {
        setError(result.problem.title);
        if (result.problem.status !== 0 && result.problem.status < 500) operation.current = null;
      } else {
        operation.current = null;
        requestedBaseline.current = current?.id;
        setRequested(result.value.state !== 'available');
        if (result.value.state !== 'available') onRequested?.(kind, format, current?.id);
        onRecheck?.();
      }
    } catch {
      if (scope === generation.current)
        setError('Não foi possível conectar ao serviço. Tente novamente.');
    } finally {
      if (scope === generation.current) setBusy(false);
    }
  };
  return (
    <section
      className="nev-document"
      aria-label={
        kind === 'participant-initial' ? 'PDF do ponto de partida' : 'PDF do resultado final'
      }
    >
      <h3>
        {kind === 'participant-initial' ? 'Guarde seu mapa inicial' : 'Guarde seu resultado final'}
      </h3>
      <label>
        Formato do PDF
        <select
          value={format}
          disabled={busy}
          onChange={(event) => {
            setFormat(event.target.value as DocumentFormat);
            setError(null);
            setRequested(false);
          }}
        >
          <option value="mobile">Leitura no celular</option>
          <option value="a4">Impressão em A4</option>
        </select>
      </label>
      <p role="status" aria-live="polite">
        {queryFailed
          ? 'Não conseguimos verificar o PDF agora. Seu resultado continua disponível nesta página.'
          : awaitingArtifact
            ? 'Preparação solicitada. O PDF aparecerá aqui quando estiver pronto.'
            : current
              ? STATUS[current.state]
              : requested
                ? 'Preparação solicitada. O PDF aparecerá aqui quando estiver pronto.'
                : 'Prepare uma versão do seu PDF para guardar.'}
      </p>
      {current?.state === 'available' && onDownload && (
        <Button
          type="button"
          disabled={downloadingId != null}
          onClick={() => onDownload(current.id)}
        >
          <Icon name="download" />
          {downloadingId === current.id ? 'Baixando…' : 'Baixar PDF'}
        </Button>
      )}
      {error && <p role="alert">{error}</p>}
      {current?.template_version && (
        <p className="nev-meta">Versão do documento: {current.template_version}.</p>
      )}
      {participationId && !pending && (
        <Button
          type="button"
          variant={current?.state === 'available' ? 'outline' : 'primary'}
          disabled={busy}
          onClick={() => {
            void requestDocument();
          }}
        >
          {busy
            ? 'Solicitando…'
            : current?.state === 'available'
              ? 'Preparar versão atual'
              : current?.state === 'failed' || error
                ? 'Tentar novamente'
                : 'Preparar PDF'}
        </Button>
      )}
      {queryFailed && onRecheck && (
        <Button type="button" variant="outline" onClick={onRecheck}>
          Verificar novamente
        </Button>
      )}
    </section>
  );
}
